import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContext";

const DEFAULT_PHONE = "(786) 774-6692";

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const { content } = useSiteContent();
  const PHONE = content?.phone || DEFAULT_PHONE;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/listings", label: t.nav.listings },
    { to: "/neighborhoods", label: t.nav.neighborhoods },
    { to: "/developments", label: t.nav.developments },
    { to: "/valuation", label: t.nav.valuation },
    { to: "/testimonials", label: t.nav.testimonials },
    { to: "/contact", label: t.nav.contact },
  ];

  const light = scrolled || open;

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          light
            ? "bg-cream/90 backdrop-blur-xl border-b border-sand/70 shadow-[0_1px_20px_rgba(13,19,26,0.06)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="shrink-0 group">
            <span className={`font-serif text-xl sm:text-2xl tracking-tight transition-colors duration-500 ${light ? "text-ink" : "text-cream"}`}>
              Carlos <span className="italic text-gold">Vindel</span>
            </span>
            <span className={`block text-[9px] uppercase tracking-[0.35em] transition-colors duration-500 ${light ? "text-ink/50" : "text-cream/60"}`}>
              Miami · South Florida
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5" data-testid="nav-links-desktop">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline-gold whitespace-nowrap text-[13px] font-medium tracking-wide transition-colors duration-500 ${
                    isActive ? "text-gold" : light ? "text-ink/80 hover:text-ink" : "text-cream/85 hover:text-cream"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className={`flex items-center gap-1.5 text-xs font-semibold tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-500 ${
                light ? "border-sand text-ink/70 hover:border-gold hover:text-gold-dark" : "border-cream/30 text-cream/80 hover:border-gold hover:text-gold"
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "en" ? "ES" : "EN"}
            </button>
            <a
              data-testid="nav-phone"
              href={`tel:+1${PHONE.replace(/\D/g, "")}`}
              className={`hidden xl:flex items-center gap-2 whitespace-nowrap text-[13px] font-medium transition-colors duration-500 ${light ? "text-ink/80 hover:text-gold-dark" : "text-cream/85 hover:text-gold"}`}
            >
              <Phone className="w-3.5 h-3.5" />
              {PHONE}
            </a>
            <Link
              to="/contact"
              data-testid="nav-connect-btn"
              className="hidden sm:inline-flex items-center whitespace-nowrap bg-gold hover:bg-gold-dark text-ink text-[13px] font-semibold tracking-wide px-5 py-2.5 rounded-full transition-colors duration-300"
            >
              {t.nav.connect}
            </Link>
            <button
              data-testid="nav-menu-btn"
              onClick={() => setOpen(!open)}
              aria-label={open ? t.nav.close : t.nav.menu}
              className={`lg:hidden p-2 transition-colors duration-500 ${light ? "text-ink" : "text-cream"}`}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink grain-overlay lg:hidden flex flex-col justify-center px-8"
          >
            <nav className="space-y-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block font-serif text-3xl sm:text-4xl py-2 ${isActive ? "text-gold italic" : "text-cream hover:text-gold"} transition-colors`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-10 pt-8 border-t border-white/10 flex items-center gap-4"
            >
              <a href={`tel:+1${PHONE.replace(/\D/g, "")}`} data-testid="nav-mobile-phone" className="text-cream/70 text-sm flex items-center gap-2">
                <Phone className="w-4 h-4 text-gold" /> {PHONE}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
